import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, CircleCheck, Home as HomeIcon, Warehouse, Hammer, Wrench, Star, MapPin, Building2, Trees } from "lucide-react";
import { useSite, useCompany } from "@/context/SiteContext";
import { useSeo, localBusinessLd, graph } from "@/hooks/useSeo";
import { Img, SectionHead, LoopVideo, HoverVideo, loopFor } from "@/components/site/Media";
import { mediaUrl } from "@/lib/api";
import { ProjectCard, Loading } from "@/components/site/Blocks";

export const ICONS = { home: HomeIcon, warehouse: Warehouse, hammer: Hammer, wrench: Wrench, building: Building2, trees: Trees };
const VALUE_ICONS = [ShieldCheck, Users, CircleCheck];

function HeroMedia({ h }) {
  const video = h.video || h.video_url;
  if (video) {
    return (
      <LoopVideo className="absolute inset-0 -z-10 h-full w-full object-cover object-[70%_center]" src={video} poster={mediaUrl(h.image, "lg")} testid="hero-video" />
    );
  }
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden">
      <Img src={h.image} alt="Southwest Florida custom home at golden hour" eager size="lg" className="ib-kenburns h-full w-full object-[70%_center]" data-testid="hero-image" />
    </div>
  );
}

export function GoogleBadge({ large = false }) {
  const { site } = useSite();
  const g = site?.settings?.google_reviews;
  if (!g?.show || !g.count) return null;
  return (
    <a href={g.url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-3 rounded-md border border-black/10 bg-white/90 backdrop-blur ${large ? "px-6 py-4" : "px-4 py-2.5"} transition-shadow hover:shadow-lg`} data-testid="google-rating-badge">
      <span className={`font-display font-bold text-[#4285F4] ${large ? "text-3xl" : "text-xl"}`}>G</span>
      <span className={`font-display ${large ? "text-3xl" : "text-xl"}`}>{Number(g.rating).toFixed(1)}</span>
      <span className="flex text-[#FBBC04]">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={large ? 20 : 15} fill="currentColor" strokeWidth={0} />)}</span>
      <span className="text-sm text-black/65">{g.count} Google reviews</span>
    </a>
  );
}

export function GoogleLinks() {
  const { site } = useSite();
  const g = site?.settings?.google_reviews;
  if (!g?.show) return null;
  return (
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      {g.url && <a href={g.url} target="_blank" rel="noreferrer" className="ib-btn ib-btn-outline" data-testid="read-google-reviews-btn">Read our Google reviews</a>}
      {g.write_url && <a href={g.write_url} target="_blank" rel="noreferrer" className="ib-btn ib-btn-primary" data-testid="write-google-review-btn">Review us on Google</a>}
    </div>
  );
}

function Hero({ h }) {
  return (
    <section className="relative isolate min-h-[560px] overflow-hidden lg:min-h-[640px]" data-testid="home-hero">
      <HeroMedia h={h} />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#f5f3f0]/95 via-[#f5f3f0]/80 to-[#f5f3f0]/30 sm:to-transparent sm:via-[#f5f3f0]/45" />
      <div className="mx-auto max-w-[1400px] px-5 py-20 sm:py-28 lg:px-8">
        <h1 className="ib-reveal font-display text-5xl font-semibold uppercase leading-[0.98] text-[color:var(--ib-charcoal)] sm:text-6xl lg:text-7xl" data-testid="hero-title">
          {h.title1}<br />{h.title2}<br /><span className="text-[color:var(--ib-orange)]">{h.title3}</span>
        </h1>
        <p className="ib-reveal mt-6 max-w-md text-base leading-relaxed text-[color:var(--ib-ink)] sm:text-lg" style={{ animationDelay: ".12s" }} data-testid="hero-subtitle">{h.subtitle}</p>
        <div className="ib-reveal mt-8 flex flex-wrap gap-4" style={{ animationDelay: ".22s" }}>
          <Link to={h.cta1_link || "/services"} className="ib-btn ib-btn-primary" data-testid="hero-cta-primary">{h.cta1_label}</Link>
          <Link to={h.cta2_link || "/projects"} className="ib-btn ib-btn-outline" data-testid="hero-cta-secondary">{h.cta2_label}</Link>
        </div>
        <div className="ib-reveal mt-6" style={{ animationDelay: ".3s" }}><GoogleBadge /></div>
      </div>
    </section>
  );
}

function ServiceCard({ s }) {
  const [hover, setHover] = useState(false);
  const Icon = ICONS[s.icon] || HomeIcon;
  const loop = loopFor(s.card_image);
  return (
    <Link to={`/services/${s.slug}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="ib-zoom group flex flex-col border border-black/10 bg-white transition-shadow hover:shadow-xl" data-testid={`service-card-${s.slug}`}>
      <div className="relative aspect-[3/2] overflow-hidden">
        <Img src={s.card_image} alt={s.title} className="h-full w-full" />
        {loop && <HoverVideo src={loop} active={hover} testid={`service-card-video-${s.slug}`} />}
      </div>
            <div className="flex flex-1 gap-4 p-6">
              <Icon className="mt-1 h-9 w-9 shrink-0 text-[color:var(--ib-orange)]" strokeWidth={1.4} />
              <div className="flex flex-1 flex-col">
                <h3 className="font-display text-xl uppercase leading-tight">{s.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-black/70">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[color:var(--ib-orange)] transition-[gap] group-hover:gap-2">Learn more <ArrowRight size={14} /></span>
              </div>
            </div>
    </Link>
  );
}

export function ServiceCards({ services }) {
  return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{services.map((s) => <ServiceCard key={s.id} s={s} />)}</div>;
}

function AboutBand({ a }) {
  return (
    <section className="ib-grain relative isolate overflow-hidden bg-[color:var(--ib-charcoal)] text-white" data-testid="home-about">
      <Img src={a.image} alt="" className="absolute inset-0 -z-10 h-full w-full opacity-60" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/90 via-black/50 to-black/80" />
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-20 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <div>
          <p className="ib-eyebrow">{a.eyebrow}</p>
          <h2 className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight sm:text-4xl lg:text-5xl">{a.title}</h2>
          <p className="mt-6 max-w-lg leading-relaxed text-white/80">{a.body}</p>
          <Link to={a.cta_link || "/process"} className="ib-btn ib-btn-primary mt-8" data-testid="home-about-cta">{a.cta_label}</Link>
        </div>
        <ul className="space-y-8 self-center">
          {(a.values || []).map((v, i) => {
            const Icon = VALUE_ICONS[i % 3];
            return (
              <li key={v.title} className="flex gap-4">
                <Icon className="h-10 w-10 shrink-0 text-[color:var(--ib-orange)]" strokeWidth={1.4} />
                <div><p className="font-display text-lg uppercase">{v.title}</p><p className="text-sm text-white/75">{v.text}</p></div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function Builder({ b }) {
  return (
    <section className="bg-white" data-testid="meet-the-builder">
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 py-20 md:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <div className="relative">
          <div className="absolute -left-3 -top-3 h-full w-full border-2 border-[color:var(--ib-orange)]" aria-hidden="true" />
          {b.video ? (
            <LoopVideo src={b.video} poster={mediaUrl(b.photo, "md")} label={`${b.name}, ${b.title}`} className="relative aspect-square w-full object-cover" testid="builder-video" />
          ) : b.photo ? <Img src={b.photo} alt={`${b.name}, ${b.title}`} className="relative aspect-square w-full object-[38%_center]" />
            : <div className="relative flex aspect-[4/5] w-full flex-col items-center justify-center bg-[color:var(--ib-sand)] text-center" data-testid="builder-photo-placeholder">
              <img src="/media/logo-icon.png" alt="" className="h-24 opacity-90" /><p className="mt-4 text-xs uppercase tracking-widest text-black/50">Photo coming soon</p>
            </div>}
        </div>
        <div>
          <p className="ib-eyebrow">{b.eyebrow}</p>
          <h2 className="mt-2 font-display text-4xl uppercase sm:text-5xl">{b.name}</h2>
          <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-black/50">{b.title}</p>
          <p className="mt-6 max-w-xl leading-relaxed text-black/75">{b.bio}</p>
          {b.quote && <blockquote className="mt-6 border-l-4 border-[color:var(--ib-orange)] pl-4 font-display text-xl uppercase text-[color:var(--ib-charcoal)]">“{b.quote}”</blockquote>}
        </div>
      </div>
    </section>
  );
}

function Featured({ f, projects }) {
  const list = projects.filter((p) => p.featured).slice(0, 5);
  if (!list.length) return null;
  return (
    <section className="bg-[color:var(--ib-off)] py-16" data-testid="home-featured-projects">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="ib-eyebrow">{f.eyebrow}</p><h2 className="mt-1 font-display text-3xl uppercase sm:text-4xl">{f.title}</h2></div>
          <Link to="/projects" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[color:var(--ib-orange)]" data-testid="view-all-projects-link">View all projects <ArrowRight size={14} /></Link>
        </div>
        <div className={`mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 ${list.length >= 5 ? "lg:grid-cols-5" : list.length === 4 ? "lg:grid-cols-4" : ""}`}>
          {list.map((p) => <ProjectCard key={p.id} p={p} tall />)}
        </div>
      </div>
    </section>
  );
}

function Why({ w }) {
  return (
    <section className="bg-white py-20" data-testid="home-why">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 lg:grid-cols-[1fr_1.6fr] lg:px-8">
        <div><p className="ib-eyebrow">{w.eyebrow}</p><h2 className="mt-2 font-display text-3xl uppercase sm:text-4xl">{w.title}</h2></div>
        <div className="grid gap-px bg-black/10 sm:grid-cols-2">
          {(w.items || []).map((it, i) => (
            <div key={it.title} className="bg-white p-7">
              <span className="font-display text-4xl text-[color:var(--ib-orange)]/30">0{i + 1}</span>
              <p className="mt-2 font-display text-xl uppercase">{it.title}</p>
              <p className="mt-2 text-sm text-black/70">{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ProcessGrid({ steps }) {
  return (
    <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {steps.map((s, i) => (
        <li key={s.id} className="relative border-t-2 border-[color:var(--ib-orange)] bg-white p-7" data-testid={`process-step-${i + 1}`}>
          <span className="font-display text-5xl text-[color:var(--ib-charcoal)]/10">{String(s.number || i + 1).padStart(2, "0")}</span>
          <h3 className="mt-1 font-display text-xl uppercase">{s.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-black/70">{s.text}</p>
        </li>
      ))}
    </ol>
  );
}

function shortName(r) {
  if (r.show_name) return r.name;
  const parts = (r.name || "").replace(/\s+-\s+.*$/, "").trim().split(/\s+/);
  if (!parts[0]) return "Verified customer";
  const first = parts[0][0].toUpperCase() + parts[0].slice(1);
  return parts.length > 1 ? `${first} ${parts[parts.length - 1][0].toUpperCase()}.` : first;
}

export function ReviewList({ reviews }) {
  const { site } = useSite();
  const googleUrl = site?.settings?.google_reviews?.url;
  if (!reviews.length) return <p className="mx-auto max-w-xl text-center text-black/60" data-testid="reviews-empty">Customer reviews will appear here once approved. Worked with Ironbark? We'd love to hear from you.</p>;
  return (
    <div className="grid items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
      {reviews.map((r) => <ReviewCard key={r.id} r={r} googleUrl={googleUrl} />)}
    </div>
  );
}

function ReviewCard({ r, googleUrl }) {
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  const textRef = useRef(null);
  useEffect(() => {
    const el = textRef.current;
    const measure = () => { if (el && !open) setLong(el.scrollHeight > el.clientHeight + 2); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, r.text]);
  return (
        <figure className="flex flex-col border border-black/10 bg-white p-7" data-testid={`review-${r.id}`}>
          {r.rating ? <div className="flex gap-0.5 text-[color:var(--ib-orange)]">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={16} fill="currentColor" />)}</div> : null}
          <blockquote ref={textRef} className={`mt-3 min-h-[11.5rem] whitespace-pre-line leading-relaxed text-black/80 ${open ? "" : "line-clamp-[7]"}`} data-testid={`review-text-${r.id}`}>“{r.text}”</blockquote>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} tabIndex={long || open ? 0 : -1} aria-hidden={!(long || open)} className={`mt-2 self-start text-xs font-bold uppercase tracking-wider text-[color:var(--ib-charcoal)] underline underline-offset-4 hover:text-[color:var(--ib-orange)] ${long || open ? "" : "invisible"}`} data-testid={`review-toggle-${r.id}`}>{open ? "Show less" : "Read more"}</button>
          {(r.review_url || (r.text?.endsWith("…") && googleUrl)) && <a href={r.review_url || googleUrl} target="_blank" rel="noreferrer" className="mt-2 text-xs font-bold uppercase tracking-wider text-[color:var(--ib-orange)]" data-testid={`review-google-link-${r.id}`}>View on Google</a>}
          <figcaption className="mt-5 text-sm font-semibold uppercase tracking-widest">{shortName(r)}{r.source && <span className="ml-2 font-normal normal-case tracking-normal text-black/50">via {r.source}</span>}</figcaption>
          {r.video_url && <a href={r.video_url} target="_blank" rel="noreferrer" className="mt-2 text-xs font-bold uppercase text-[color:var(--ib-orange)]">Watch video review</a>}
        </figure>
  );
}

function Areas({ s, areas, company }) {
  return (
    <section className="bg-white py-16" data-testid="home-service-areas">
      <div className="mx-auto max-w-[1400px] px-5 text-center lg:px-8">
        <SectionHead eyebrow={s.eyebrow} title={s.title} />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {areas.map((a) => <Link key={a.id} to={`/service-areas/${a.slug}`} className="inline-flex items-center gap-2 rounded-md border border-black/15 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors hover:border-[color:var(--ib-orange)] hover:text-[color:var(--ib-orange)]"><MapPin size={14} />{a.name}</Link>)}
        </div>
        <p className="mt-6 text-sm text-black/60">{company.service_area_summary}</p>
      </div>
    </section>
  );
}

export default function Home() {
  const { site } = useSite();
  const company = useCompany();
  useSeo("home", { jsonLd: site ? graph(localBusinessLd(company, site.service_areas, site)) : null });
  if (!site) return <Loading />;
  const c = site.content;
  return (
    <main>
      <Hero h={c.hero || {}} />
      <Builder b={c.builder || {}} />
      <section className="bg-[color:var(--ib-off)] py-16" data-testid="home-services">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHead eyebrow={c.services_section?.eyebrow} title={c.services_section?.title} />
          <div className="mt-10"><ServiceCards services={site.services} /></div>
        </div>
      </section>
      <AboutBand a={c.about_home || {}} />
      <Featured f={c.featured || {}} projects={site.projects} />
      <Why w={c.why || {}} />
      <section className="bg-[color:var(--ib-off)] py-20" data-testid="home-process">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHead eyebrow={c.process_section?.eyebrow} title={c.process_section?.title} />
          <div className="mt-10"><ProcessGrid steps={site.process_steps} /></div>
        </div>
      </section>
      <section className="bg-[color:var(--ib-sand)] py-20" data-testid="home-reviews">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHead eyebrow={c.reviews_section?.eyebrow} title={c.reviews_section?.title} />
          <div className="mt-8 flex justify-center"><GoogleBadge large /></div>
          {site.reviews.length > 0 && <div className="mt-10"><ReviewList reviews={site.reviews.slice(0, 3)} /></div>}
          <GoogleLinks />
        </div>
      </section>
      <Areas s={c.areas_section || {}} areas={site.service_areas} company={company} />
    </main>
  );
}
